/**
 * The CONTROLS section of PREFERENCES: three screens, a live device feed and
 * the two flows that need one — calibration and learning a button.
 *
 * Split out of `preferences-surface.ts` because it is the one part of that
 * panel with a **clock**. Every other setting is a value that changes when a
 * chip is clicked; a joystick has to be watched, sixty times a second, while
 * the player pushes it around. The polling belongs somewhere that can be handed
 * a fake device and stepped by hand in a test, which is what `poll()` is.
 *
 * ## The panel swallows the very device it is measuring
 *
 * The stick's buttons do nothing but confirm while this is open, and that is
 * not a nicety. `docs/engineering/lessons.md` and `keys.ts`'s
 * `UI_OVERLAY_CLASSES` both record the shape of the bug it avoids: a surface
 * over the flight view that the input layer does not know about lets input
 * through, and the thing behind it is a trigger. A calibration screen is the
 * worst case of that trap, because the player is being *told* to work the
 * controls — so it must be impossible for the flow to fire a gun, and the
 * button that confirms a step is the same button that would have.
 *
 * The block itself is one predicate, at the route (`blocked` in
 * `gamepad.ts`'s options): while PREFERENCES is up, the frame the sim reads
 * carries no axes, no held buttons and no commands. `snapshot()` keeps
 * working, and is what this file reads. Nothing here can leak, because nothing
 * here is on the path the sim reads from.
 */

import {
  beginCalibration,
  calibratedMapping,
  calibrationReference,
  clearButton,
  confirmCalibration,
  learnButton,
  sampleCalibration,
  skipCalibrationStep,
  type CalibrationState,
} from "../flight/gamepad-calibration.ts";
import { NO_PAD_AXES, padAxesFrom, type PadAxes, type PadSnapshot } from "../flight/gamepad-axes.ts";
import {
  AXIS_ROLES,
  DEAD_ZONE_STEP,
  defaultMapping,
  MAX_DEAD_ZONE,
  PAD_ACTIONS,
  RESPONSE_CURVES,
  type AxisRole,
  type GamepadMapping,
  type PadAction,
} from "../flight/gamepad-mapping.ts";
import { bindingsScreen, calibrateScreen, controlsScreen, movementLabel } from "./model/controls-screen.ts";
import type { ShellScreen } from "./model/shell-screen.ts";

/** The slice of `gamepad.ts`'s controls this section needs — the live device, minus anything that flies. */
export interface GamepadFeed {
  snapshot(): PadSnapshot | undefined;
  mapping(): GamepadMapping | undefined;
  setMapping(next: GamepadMapping): void;
}

export type ControlsView = "controls" | "bindings" | "calibrate";

/** `undefined` means the command was not this section's; `"back"` means return to PREFERENCES. */
export type ControlsResult = "handled" | "back" | undefined;

/** Steps the dead zone and wraps at the top, so one chip walks the whole range. */
function nextDeadZone(deadZone: number): number {
  const stepped = deadZone + DEAD_ZONE_STEP;
  // A whole percent, for the reason `preferences.ts`'s volume step is rounded:
  // 0.1 + 0.05 is 0.15000000000000002, and a value walked round the loop must
  // come back to the number it started on rather than a drifting neighbour.
  return stepped > MAX_DEAD_ZONE + 1e-9 ? 0 : Math.round(stepped * 100) / 100;
}

export class ControlsSection {
  private view: ControlsView = "controls";
  private axes: PadAxes = NO_PAD_AXES;
  private raw: readonly number[] = [];
  private lastButtons: readonly boolean[] = [];
  private learning: PadAction | undefined;
  private calibration: CalibrationState | undefined;

  constructor(private readonly feed: GamepadFeed | undefined) {}

  /** Back to the front screen, with nothing half-done — what re-opening the panel gets. */
  reset(): void {
    this.view = "controls";
    this.learning = undefined;
    this.calibration = undefined;
    this.axes = NO_PAD_AXES;
  }

  screen(): ShellScreen {
    const mapping = this.feed?.mapping();
    if (this.view === "calibrate" && this.calibration) {
      return calibrateScreen(this.calibration, movementLabel(this.raw, calibrationReference(this.calibration)));
    }
    if (this.view === "bindings" && mapping) return bindingsScreen(mapping, this.learning);
    return controlsScreen({ device: mapping?.deviceId, mapping, axes: this.axes, learning: this.learning });
  }

  /**
   * One poll of the device. Returns whether the screen needs repainting, so a
   * still stick over a paused sortie costs one `getGamepads()` call a frame and
   * nothing else.
   */
  poll(): boolean {
    const snapshot = this.feed?.snapshot();
    if (!snapshot) return this.forget();
    const mapping = this.feed?.mapping();
    const previous = this.axes;
    const previousRaw = this.raw;
    this.axes = mapping ? padAxesFrom(snapshot, mapping) : NO_PAD_AXES;
    // Copied, not aliased. Some browsers hand back the *same* arrays on every
    // `getGamepads()` and mutate them in place, and a previous-frame reading
    // that is the current-frame reading can never show an edge — the button
    // half of that cost this suite a test to find.
    this.raw = [...snapshot.axes];
    const acted = this.consumePress(this.risingEdges(snapshot));
    if (this.calibration) this.calibration = sampleCalibration(this.calibration, snapshot.axes);
    // The calibration screen prints the raw movement rather than the calibrated
    // reading — it is running precisely because the calibrated one is not
    // trustworthy yet — so it repaints on the raw numbers instead.
    const moved = this.view === "calibrate" ? !sameNumbers(previousRaw, this.raw) : !sameAxes(previous, this.axes);
    return acted || moved;
  }

  /** Nothing is plugged in: drop anything the last device left behind. */
  private forget(): boolean {
    if (this.axes === NO_PAD_AXES && this.raw.length === 0) return false;
    this.axes = NO_PAD_AXES;
    this.raw = [];
    this.lastButtons = [];
    return true;
  }

  /** Button indices that went down since the previous poll. */
  private risingEdges(snapshot: PadSnapshot): readonly number[] {
    const edges: number[] = [];
    for (let index = 0; index < snapshot.buttons.length; index++) {
      if (snapshot.buttons[index] === true && this.lastButtons[index] !== true) edges.push(index);
    }
    this.lastButtons = [...snapshot.buttons];
    return edges;
  }

  /**
   * What a press means on this screen. Only the two flows that are *asking* for
   * one consume it — everywhere else a stick button does nothing, because the
   * panel is not a place to fly from.
   */
  private consumePress(pressed: readonly number[]): boolean {
    const first = pressed[0];
    if (first === undefined) return false;
    if (this.learning !== undefined) return this.bind(this.learning, first);
    // On the calibration screen the stick's own buttons confirm the step, which
    // is the only sensible thing for a flow that has both the player's hands on
    // the controls it is measuring.
    if (this.view === "calibrate") return this.confirm();
    return false;
  }

  private bind(action: PadAction, button: number): boolean {
    const mapping = this.feed?.mapping();
    if (!mapping) return false;
    this.feed?.setMapping(learnButton(mapping, action, button));
    this.learning = undefined;
    return true;
  }

  private confirm(): boolean {
    if (!this.calibration) return false;
    if (this.calibration.step === "done") return this.keepCalibration();
    this.calibration = confirmCalibration(this.calibration, this.raw);
    return true;
  }

  private keepCalibration(): boolean {
    const mapping = this.feed?.mapping();
    if (!this.calibration || !mapping) return false;
    this.feed?.setMapping(calibratedMapping(this.calibration, mapping));
    this.calibration = undefined;
    this.view = "controls";
    return true;
  }

  private edit(next: GamepadMapping): ControlsResult {
    this.feed?.setMapping(next);
    return "handled";
  }

  private invert(role: AxisRole): ControlsResult {
    const mapping = this.feed?.mapping();
    if (!mapping) return "handled";
    const axis = mapping.axes[role];
    return this.edit({ ...mapping, axes: { ...mapping.axes, [role]: { ...axis, invert: !axis.invert } } });
  }

  private restore(): ControlsResult {
    const snapshot = this.feed?.snapshot();
    const mapping = this.feed?.mapping();
    if (!snapshot || !mapping) return "handled";
    return this.edit(defaultMapping(mapping.deviceId, snapshot.standard, snapshot.axes.length));
  }

  private show(view: ControlsView): ControlsResult {
    this.view = view;
    // A button held on the way in must not immediately count as a press in the
    // flow being entered — the same rule `keyboard.ts` gets from `event.repeat`.
    this.lastButtons = [...(this.feed?.snapshot()?.buttons ?? [])];
    return "handled";
  }

  /** The section's own commands. `undefined` for anything it does not own, so the caller can fall through. */
  run(name: string): ControlsResult {
    if (name === "ctl:accept") return "back";
    if (name === "ctl:bindings-accept") {
      this.learning = undefined;
      return this.show("controls");
    }
    if (name === "ctl:buttons") return this.show("bindings");
    if (name === "ctl:calibrate") {
      this.calibration = beginCalibration(this.feed?.snapshot()?.axes.length ?? 0);
      return this.show("calibrate");
    }
    return this.runEdit(name);
  }

  private runEdit(name: string): ControlsResult {
    const mapping = this.feed?.mapping();
    if (name === "ctl:cal:confirm") {
      // `confirm` keeps the measurement itself once the flow reaches `done`.
      this.confirm();
      return "handled";
    }
    if (name === "ctl:cal:skip") {
      if (this.calibration) this.calibration = skipCalibrationStep(this.calibration);
      return "handled";
    }
    if (name === "ctl:cal:cancel") {
      this.calibration = undefined;
      return this.show("controls");
    }
    if (name === "ctl:restore") {
      this.learning = undefined;
      return this.restore();
    }
    if (name === "ctl:clear") {
      if (mapping && this.learning) this.feed?.setMapping(clearButton(mapping, this.learning));
      this.learning = undefined;
      return "handled";
    }
    if (name.startsWith("ctl:learn:")) {
      const action = PAD_ACTIONS.find((candidate) => candidate === name.slice("ctl:learn:".length));
      this.learning = action;
      this.lastButtons = [...(this.feed?.snapshot()?.buttons ?? [])];
      return "handled";
    }
    if (name.startsWith("ctl:invert:")) {
      const role = AXIS_ROLES.find((candidate) => candidate === name.slice("ctl:invert:".length));
      return role ? this.invert(role) : "handled";
    }
    if (name === "ctl:deadzone" && mapping) return this.edit({ ...mapping, deadZone: nextDeadZone(mapping.deadZone) });
    if (name === "ctl:curve" && mapping) {
      const at = RESPONSE_CURVES.indexOf(mapping.curve);
      return this.edit({ ...mapping, curve: RESPONSE_CURVES[(at + 1) % RESPONSE_CURVES.length] ?? "linear" });
    }
    return name.startsWith("ctl:") ? "handled" : undefined;
  }
}

function sameAxes(a: PadAxes, b: PadAxes): boolean {
  return a.pitch === b.pitch && a.roll === b.roll && a.yaw === b.yaw && a.throttle === b.throttle;
}

function sameNumbers(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}
